import {
  Box,
  Button,
  Typography,
  Container,
  Chip,
  Stack,
  IconButton,
  Avatar,
  Card,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function MainFrameComponent({ theme }) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Dados das funcionalidades para o carrossel
  const features = [
    {
      id: 'auditoria',
      title: 'Auditoria Inteligente',
      subtitle: 'Monitore Tudo em Tempo Real',
      description: 'Relatórios automáticos que identificam problemas antes que afetem suas vendas',
      image: require('../../../assets/auditoria/auditoria_media_png.png'),
      icon: <AssessmentIcon sx={{ fontSize: 32 }} />,
      color: '#1A73E8',
      gradient: 'linear-gradient(135deg, #1A73E8 0%, #1557B0 100%)',
      stats: 'Reduz 60% do tempo de análise',
      benefits: ['Alertas instantâneos', 'Dashboards interativos', 'Previsões de tendências']
    },
    {
      id: 'automacoes',
      title: 'Automações Avançadas',
      subtitle: 'Trabalhe Menos, Venda Mais',
      description: 'IA que automatiza tarefas repetitivas e otimiza processos 24/7',
      image: require('../../../assets/automacoes/automacoes_png.png'),
      icon: <AutoFixHighIcon sx={{ fontSize: 32 }} />,
      color: '#FF7323',
      gradient: 'linear-gradient(135deg, #FF7323 0%, #D85A00 100%)',
      stats: 'Economiza 40h semanais',
      benefits: ['Preços dinâmicos', 'Gestão de estoque', 'Processamento automático']
    },
    {
      id: 'margem',
      title: 'Análise de Margem',
      subtitle: 'Maximize Seus Lucros',
      description: 'Controle total da lucratividade com ajustes inteligentes em tempo real',
      image: require('../../../assets/margem/margem_media_png.png'),
      icon: <AttachMoneyIcon sx={{ fontSize: 32 }} />,
      color: '#38A169',
      gradient: 'linear-gradient(135deg, #38A169 0%, #2F855A 100%)',
      stats: 'Aumenta 30% na lucratividade',
      benefits: ['Monitoramento em tempo real', 'Alertas de margem baixa', 'Otimização automática']
    },
    {
      id: 'fretegratis',
      title: 'Otimização de Frete',
      subtitle: 'Converta Mais Vendas',
      description: 'Estratégias inteligentes de frete que aumentam conversão sem perder margem',
      image: require('../../../assets/fretegratis/fretegratis_png.png'),
      icon: <LocalShippingIcon sx={{ fontSize: 32 }} />,
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
      stats: 'Aumenta 25% nas conversões',
      benefits: ['Cálculo otimizado', 'Estratégias de pricing', 'ROI garantido']
    },
    {
      id: 'etiquetas',
      title: 'Gestão de Etiquetas',
      subtitle: 'Organize com Eficiência',
      description: 'Sistema inteligente que categoriza e organiza produtos automaticamente',
      image: require('../../../assets/etiquetas/etiquetas_media_png.png'),
      icon: <LocalOfferIcon sx={{ fontSize: 32 }} />,
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
      stats: 'Melhora 35% na organização',
      benefits: ['Categorização automática', 'Controle de qualidade', 'Gestão visual']
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % features.length);
      }, 4000); // 4 segundos por slide

      return () => clearInterval(intervalRef.current);
    }
  }, [isPlaying, features.length]);

  const handlePrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % features.length);
  };

  const handleSlideClick = (index) => {
    setCurrentSlide(index);
  };

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  const currentFeature = features[currentSlide];

  // Estatísticas de impacto para vendas
  const impactStats = [
    { value: "R$ 100M+", label: "Faturamento Gerenciado", icon: "💰" },
    { value: "1", label: "Empresas Atendidas", icon: "🏢" },
    { value: "40%", label: "Aumento Médio de Lucro", icon: "📈" },
    { value: "24/7", label: "Monitoramento Contínuo", icon: "🔄" },
  ];

  return (
    <Box
      sx={{
        minHeight: { xs: "auto", md: "90vh" },
        background: `
          linear-gradient(135deg, 
            rgba(255, 115, 35, 0.04) 0%, 
            rgba(26, 115, 232, 0.03) 30%,
            rgba(37, 211, 102, 0.02) 70%, 
            rgba(255, 255, 255, 0.05) 100%
          ),
          radial-gradient(circle at 20% 30%, rgba(255, 115, 35, 0.08) 0%, transparent 60%),
          radial-gradient(circle at 80% 70%, rgba(26, 115, 232, 0.06) 0%, transparent 60%)
        `,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        py: { xs: 3, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Enhanced Layout with Carousel */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'center',
            gap: { xs: 3, sm: 4, lg: 6 },
            minHeight: { xs: "auto", md: "80vh" },
          }}
        >
          {/* Left Content - Sales Focused */}
          <Box sx={{ 
            flex: 1, 
            maxWidth: { lg: '55%' }, 
            width: { xs: '100%', lg: '55%' },
            minHeight: { xs: 'auto', lg: '600px' } 
          }}>
            <Box
              sx={{
                transform: isVisible ? "translateY(0)" : "translateY(50px)",
                opacity: isVisible ? 1 : 0,
                transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', lg: 'center' },
                textAlign: { xs: 'center', lg: 'left' },
                px: { xs: 1, sm: 2, lg: 0 },
              }}
            >
              {/* Strategic Badge */}
              <Chip
                label="🏆 Líder em IA para E-commerce"
                sx={{
                  mb: { xs: 2, md: 3 },
                  backgroundColor: "rgba(255, 115, 35, 0.1)",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  padding: { xs: "8px 16px", md: "12px 20px" },
                  borderRadius: "50px",
                  border: "2px solid rgba(255, 115, 35, 0.2)",
                  boxShadow: "0 4px 15px rgba(255, 115, 35, 0.2)",
                  alignSelf: { xs: 'center', lg: 'flex-start' },
                }}
              />

              {/* Main Headline */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.8rem", lg: "4.5rem" },
                  fontWeight: 900,
                  lineHeight: { xs: 1.2, md: 1.1 },
                  mb: { xs: 1.5, md: 2 },
                  background: "linear-gradient(135deg, #FF7323 0%, #D85A00 50%, #1A73E8 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Wala Smart Work
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.8rem" },
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: { xs: 1, md: 1.5 },
                  lineHeight: { xs: 1.3, md: 1.2 },
                }}
              >
                Transforme seu E-commerce com{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.primary.main,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      height: { xs: "2px", md: "3px" },
                      background: "linear-gradient(90deg, #FF7323, #D85A00)",
                      borderRadius: "2px",
                    },
                  }}
                >
                  Inteligência Artificial
                </Box>
              </Typography>

              {/* Value Proposition */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.2rem" },
                  color: theme.palette.text.secondary,
                  mb: { xs: 3, md: 4 },
                  lineHeight: { xs: 1.5, md: 1.7 },
                  maxWidth: { xs: "100%", md: "600px" },
                  mx: { xs: 'auto', lg: 0 },
                }}
              >
                Automatize processos, monitore performance em tempo real e{" "}
                <strong>maximize seus lucros</strong> com a plataforma mais avançada
                para gestão de e-commerce do Brasil.
              </Typography>

              {/* Impact Stats Row */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                  gap: { xs: 1.5, sm: 2 },
                  mb: { xs: 3, md: 4 },
                }}
              >
                {impactStats.map((stat, index) => (
                  <Box
                    key={index}
                    sx={{
                      textAlign: "center",
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: { xs: "12px", md: "16px" },
                      background: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(15px)",
                      border: "1px solid rgba(255, 115, 35, 0.15)",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontSize: { xs: "1.2rem", md: "1.5rem" }, 
                        mb: { xs: 0.3, md: 0.5 } 
                      }}
                    >
                      {stat.icon}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.primary.main,
                        fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
                        mb: 0.3,
                        lineHeight: 1.1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: "0.7rem", md: "0.8rem" },
                        lineHeight: 1.2,
                        display: 'block',
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Strategic CTAs */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 2 }}
                sx={{ 
                  mb: { xs: 3, md: 4 },
                  alignItems: "center",
                  justifyContent: { xs: "center", lg: "flex-start" },
                }}
              >
                <Button
                  onClick={() => window.open("/checkout/trial", "_blank")}
                  variant="contained"
                  size="large"
                  sx={{
                    background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                    color: "white",
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    fontWeight: 700,
                    padding: { xs: "14px 28px", sm: "16px 32px", md: "18px 36px" },
                    borderRadius: { xs: "12px", md: "16px" },
                    boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.35)",
                    minWidth: { xs: "250px", sm: "auto" },
                    "&:hover": {
                      background: "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
                      transform: "translateY(-3px)",
                      boxShadow: "0px 12px 35px rgba(255, 115, 35, 0.45)",
                    },
                  }}
                >
                  🚀 Começar Agora - GRÁTIS
                </Button>

                <Button
                  onClick={() => handleScroll("features")}
                  variant="outlined"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.grey[300],
                    fontSize: { xs: "1rem", sm: "1rem", md: "1.1rem" },
                    fontWeight: 600,
                    padding: { xs: "14px 28px", sm: "16px 32px", md: "18px 36px" },
                    borderRadius: { xs: "12px", md: "16px" },
                    borderWidth: 2,
                    minWidth: { xs: "250px", sm: "auto" },
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: "rgba(255, 115, 35, 0.05)",
                      borderWidth: 2,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Ver Demonstração
                </Button>
              </Stack>

              {/* Benefits Preview - Fixed Height */}
              <Box sx={{ mb: 3, minHeight: 80 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1,
                    transition: "opacity 0.5s ease",
                  }}
                >
                  💡 Funcionalidade em Destaque:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "1rem",
                    fontWeight: 500,
                    transition: "opacity 0.5s ease",
                  }}
                >
                  {currentFeature.title} - {currentFeature.stats}
                </Typography>
              </Box>

              {/* Trust Indicators */}
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.tertiary,
                  fontSize: "1rem",
                  fontWeight: 500,
                }}
              >
                ✅ Teste GRÁTIS por 14 dias • ✅ Implementação em 24h • ✅ Suporte Premium 24/7
              </Typography>
            </Box>
          </Box>

          {/* Right Side - Interactive Carousel */}
          <Box sx={{ flex: 1, maxWidth: { lg: '45%' }, width: '100%' }}>
            <Box
              sx={{
                transform: isVisible ? "translateX(0)" : "translateX(50px)",
                opacity: isVisible ? 1 : 0,
                transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
                position: "relative",
              }}
            >

              {/* Main Feature Display */}
              <Card
                sx={{
                  borderRadius: { xs: "16px", md: "24px" },
                  overflow: "hidden",
                  border: "2px solid rgba(255, 115, 35, 0.15)",
                  boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.08)",
                  position: "relative",
                  background: "linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))",
                  backdropFilter: "blur(20px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0px 25px 80px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                {/* Feature Image */}
                <Box
                  sx={{
                    height: { xs: 200, sm: 280, md: 380 },
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <img
                    key={currentFeature.id}
                    src={currentFeature.image}
                    alt={currentFeature.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "opacity 0.6s ease-in-out",
                    }}
                  />

                  {/* Overlay with Info */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: { xs: 12, md: 20 },
                      left: { xs: 12, md: 20 },
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(15px)",
                      borderRadius: { xs: "12px", md: "16px" },
                      p: { xs: 1.5, md: 2 },
                      border: "2px solid rgba(255, 115, 35, 0.2)",
                      transition: "all 0.5s ease",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 32, md: 40 },
                        height: { xs: 32, md: 40 },
                        background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                        color: "white",
                        mb: { xs: 0.5, md: 1 },
                        transition: "all 0.5s ease",
                      }}
                    >
                      {currentFeature.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.primary.main,
                        fontSize: { xs: "0.8rem", md: "1rem" },
                        transition: "all 0.5s ease",
                        lineHeight: 1.2,
                      }}
                    >
                      {currentFeature.stats}
                    </Typography>
                  </Box>
                </Box>

                {/* Feature Info */}
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      mb: 1,
                      fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                      transition: "all 0.5s ease",
                      lineHeight: 1.2,
                    }}
                  >
                    {currentFeature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.5,
                      fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                      transition: "all 0.5s ease",
                    }}
                  >
                    {currentFeature.description}
                  </Typography>
                </Box>
              </Card>

              {/* Thumbnail Navigation */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 0.8, sm: 1, md: 1.5 },
                  mt: { xs: 2, md: 3 },
                  flexWrap: "wrap",
                  px: { xs: 1, sm: 0 },
                }}
              >
                {features.map((feature, index) => (
                  <Box
                    key={feature.id}
                    onClick={() => handleSlideClick(index)}
                    sx={{
                      cursor: "pointer",
                      width: { xs: 40, sm: 50, md: 60 },
                      height: { xs: 40, sm: 50, md: 60 },
                      borderRadius: { xs: "8px", md: "12px" },
                      overflow: "hidden",
                      border: currentSlide === index 
                        ? "3px solid #FF7323" 
                        : "2px solid rgba(255, 255, 255, 0.5)",
                      transition: "all 0.3s ease",
                      transform: currentSlide === index ? "scale(1.1)" : "scale(1)",
                      boxShadow: currentSlide === index 
                        ? "0 8px 25px rgba(255, 115, 35, 0.4)" 
                        : "0 4px 12px rgba(0,0,0,0.1)",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 6px 20px rgba(255, 115, 35, 0.25)",
                      },
                    }}
                  >
                    <img
                      src={feature.image}
                      alt={feature.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Progress Indicators */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: { xs: 0.8, md: 1 },
                  mt: { xs: 1.5, md: 2 },
                }}
              >
                {features.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: { xs: 6, md: 8 },
                      height: { xs: 6, md: 8 },
                      borderRadius: "50%",
                      background: currentSlide === index 
                        ? "#FF7323" 
                        : "rgba(0,0,0,0.2)",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
